import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { PhArrowRight, PhCloudSun, PhMoon, PhSun } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import ProgressBar from 'tio-ui/components/progress-bar';
import { Section, VStack } from 'tio-ui/components/layout';
import AttentionPanel from 'tio-employee/components/dashboard-kit/attention-panel';
import BenefitCard from 'tio-employee/components/dashboard-kit/benefit-card';
import DashboardHero from 'tio-employee/components/dashboard-kit/hero';
import ExploreCard from 'tio-employee/components/dashboard-kit/explore-card';
import HeroStat from 'tio-employee/components/dashboard-kit/hero-stat';
import TaskCard from 'tio-employee/components/dashboard-kit/task-card';
import RecommendationCard from 'tio-employee/components/beta/home/recommendation-card';
import BetaHomePhoneRecommendation from 'tio-employee/components/beta/home/phone-recommendation';
import BetaHomeLocaleRecommendation from 'tio-employee/components/beta/home/locale-recommendation';
import DashboardLocaleAlert from 'tio-employee/components/dashboard/locale-alert';
import { DESKTOP_QUERY } from 'tio-employee/components/app/phone-capture/prompt';
import { phoneCaptureCard } from 'tio-employee/utils/beta/phone-capture-card';
import { buildBetaHomeModel } from 'tio-employee/utils/beta/home-model';
import { betaHomeFlags } from 'tio-employee/utils/beta/home-flags';
import { benefitFlags } from 'tio-employee/utils/beta/benefit-flags';
import { benefitRows } from 'tio-employee/utils/beta/benefit-rows';
import { planSignals, syfSignals, wellnessSignals } from 'tio-employee/utils/beta/home-signals';
import type {
  BetaAttentionRow,
  BetaHomeFlags,
  BetaHomeModel,
} from 'tio-employee/utils/beta/home-model';
import type FeaturesService from 'tio-common/services/features';
import { t } from 'ember-intl';
import type { IntlService } from 'ember-intl';
import type { RecommendationItem } from 'tio-employee/components/beta/home/recommendation-card';
import type PhoneCaptureSnoozeService from 'tio-employee/services/phone-capture-snooze';
import type LocalePreferenceService from 'tio-employee/services/locale-preference';
import type LocaleRecommendationSnoozeService from 'tio-employee/services/locale-recommendation-snooze';
import type BetaBrandService from 'tio-employee/services/beta-brand';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type EmployeeService from 'tio-employee/services/employee';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
import type { BetaHomeRouteModel } from 'tio-employee/routes/authenticated/dashboard';

// Past four the panel reads as a backlog rather than as triage; the header
// keeps the full count so collapsing never hides how much is waiting.
const ATTENTION_ROWS_SHOWN = 4;

export interface BetaHomeSignature {
  Args: {
    model: BetaHomeRouteModel;
    /** The authenticated route's raw wellness payload; it gates the wellness row and dates the loan figures. */
    wellnessData?: Record<string, unknown> | null;
  };
  Element: HTMLDivElement;
}

/**
 * The Brand Updates 2026 home page body, composed from the dashboard kit. The
 * same body renders inside the app shell on `/dashboard` and inside the dark
 * full-page frame on `/beta/home-preview`; see BETA_HOME_GAPS.md for the
 * figures the backend cannot answer yet.
 */
export default class BetaHome extends Component<BetaHomeSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare partner: PartnerService;
  @service declare employee: EmployeeService;
  @service declare demoMode: DemoModeService;
  @service('beta-brand') declare betaBrand: BetaBrandService;
  @service declare features: FeaturesService;
  @service declare intl: IntlService;
  @service declare phoneCaptureSnooze: PhoneCaptureSnoozeService;
  @service declare localePreference: LocalePreferenceService;
  @service declare localeRecommendationSnooze: LocaleRecommendationSnoozeService;

  @tracked isShowingAllRows = false;

  get firstName(): string {
    return (
      this.sessionContext.user?.person?.firstName ||
      this.sessionContext.currentEmployee?.firstName ||
      ''
    );
  }

  /** The same rows the side nav renders, so the page never offers what the rail hides. */
  get flags(): BetaHomeFlags {
    const scenario = this.demoMode.scenarioFeatures;
    const rows = benefitRows(
      benefitFlags({
        partner: this.partner,
        employee: this.employee,
        sessionContext: this.sessionContext,
        wellnessData: this.args.wellnessData,
        scenario,
      })
    );
    return betaHomeFlags({ rows, partner: this.partner, features: this.features });
  }

  /**
   * Reading the employee's state here is what retires the recommendation the moment a
   * number is verified, with no reload.
   */
  get phoneCapture() {
    const state = this.sessionContext.currentEmployee?.phoneVerificationState ?? 'none';
    const { title } = phoneCaptureCard(this.intl, state);
    // The overlay's pitch: the card's own description repeats the consent line's opening.
    const description = this.intl.t('phone_verification.prompt.benefit');
    const eligible =
      this.sessionContext.user?.phoneCaptureCard?.eligible === true &&
      !this.phoneCaptureSnooze.isSnoozed;
    return { state, title, description, eligible };
  }

  // Unlike the dashboard alert, no French browser is needed: the card offers all three languages.
  get locale() {
    const eligible =
      this.partner.isUps &&
      this.localePreference.savesToAccount &&
      !this.localePreference.saved &&
      !this.localeRecommendationSnooze.isSnoozed;
    return {
      title: this.intl.t('recommendation_card.locale.title'),
      description: this.intl.t('recommendation_card.locale.description'),
      eligible,
    };
  }

  /** The model says what to recommend; each item's action and exits are wired here, by id. */
  get recommendationItems(): RecommendationItem[] {
    return this.home.recommendations.flatMap((entry) => {
      const wiring = this.#recommendationWiring[entry.id];
      return wiring ? [{ ...entry, ...wiring }] : [];
    });
  }

  #recommendationWiring: Record<
    string,
    Pick<RecommendationItem, 'action' | 'onDismiss' | 'onRemindLater'>
  > = {
    'phone-capture': {
      action: BetaHomePhoneRecommendation,
      onDismiss: () => this.phoneCaptureSnooze.snooze('soft', this.#platform, 'dashboardCard'),
      onRemindLater: () => this.phoneCaptureSnooze.snooze('long', this.#platform, 'dashboardCard'),
    },
    locale: {
      action: BetaHomeLocaleRecommendation,
      onDismiss: () => this.localeRecommendationSnooze.snooze(),
      onRemindLater: () => this.localeRecommendationSnooze.snooze(),
    },
  };

  get #platform() {
    return window.matchMedia(DESKTOP_QUERY).matches ? 'desktop' : 'mobile';
  }

  get home(): BetaHomeModel {
    const now = Date.now();
    const employee = this.sessionContext.currentEmployee;
    return buildBetaHomeModel({
      model: this.args.model.reimagined,
      firstName: this.firstName,
      hour: new Date(now).getHours(),
      now,
      actionItems: this.args.model.actionItems,
      flags: this.flags,
      phoneCapture: this.phoneCapture,
      locale: this.locale,
      tuitionYearToDate: this.args.model.tuitionYearToDate,
      contributionActivity: this.args.model.contributionActivity ?? null,
      plan: planSignals(employee?.plan),
      syf: syfSignals(
        employee?.matchParticipant,
        now,
        this.args.model.syfPaymentsToCertify ?? null
      ),
      wellness: wellnessSignals(this.args.wellnessData),
      employerName: this.sessionContext.currentCompany?.name ?? null,
    });
  }

  get greetingIcon() {
    const hour = new Date().getHours();
    if (hour < 12) return PhSun;
    if (hour < 17) return PhCloudSun;
    return PhMoon;
  }

  get greeting(): string {
    return `${this.home.greeting}, ${this.home.firstName}`;
  }

  /** The design's section heading changes with the user state. */
  get discoverHeading(): string {
    return this.home.audience === 'new' ? 'Explore Your Benefits' : 'More to Explore';
  }

  get tileGridClass(): string {
    const count = this.home.hero.tiles.length;
    if (count >= 4) return 'tio-beta-home__grid tio-beta-home__grid--quad';
    if (count === 3) return 'tio-beta-home__grid tio-beta-home__grid--triple';
    if (count === 1) return 'tio-beta-home__grid tio-beta-home__grid--single';
    return 'tio-beta-home__grid';
  }

  get visibleRows(): BetaAttentionRow[] {
    const { rows } = this.home.attention;
    return this.isShowingAllRows ? rows : rows.slice(0, ATTENTION_ROWS_SHOWN);
  }

  get collapsedRowCount(): number {
    return Math.max(0, this.home.attention.rows.length - ATTENTION_ROWS_SHOWN);
  }

  toggleAllRows = () => {
    this.isShowingAllRows = !this.isShowingAllRows;
  };

  /** Rows and steps that act in place carry no route; the fallback is never rendered for them. */
  routeFor = (row: BetaAttentionRow) => row.route ?? 'authenticated.dashboard';

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="tio-beta-home"
      data-beta-brand={{this.betaBrand.brand}}
      data-test-beta-home={{this.home.audience}}
      ...attributes
    >
      <div class="tio-beta-home__main">
        <VStack class="gap-8">
          {{! The first-run page has no Recommendation card, so the language question is asked here. }}
          {{#if (eq this.home.audience "new")}}
            <DashboardLocaleAlert />
          {{/if}}
          <DashboardHero
            @greeting={{this.greeting}}
            @icon={{this.greetingIcon}}
            @title={{this.home.hero.title}}
            @actionLabel={{this.home.hero.action.label}}
            @actionRoute={{this.home.hero.action.route}}
            @actionKind={{this.home.hero.action.kind}}
            data-test-beta-hero={{this.home.audience}}
          >
            <:default>
              {{#if this.home.hero.lede}}
                <p class="mt-2 tio-text-base-medium tio-fg-secondary" data-test-beta-hero-lede>
                  {{this.home.hero.lede}}
                </p>
              {{/if}}
            </:default>
            <:stats>
              {{#if this.home.hero.tiles.length}}
                <div class="{{this.tileGridClass}} pii">
                  {{#each this.home.hero.tiles as |tile|}}
                    <HeroStat
                      @emphasis={{tile.kind}}
                      @label={{tile.label}}
                      @value={{tile.value}}
                      @context={{tile.context}}
                      @progressValue={{tile.progressValue}}
                      @progressMax={{tile.progressMax}}
                      @body={{tile.body}}
                      @linkLabel={{tile.linkLabel}}
                      @linkRoute={{tile.linkRoute}}
                      data-test-beta-stat={{tile.id}}
                    />
                  {{/each}}
                </div>
              {{/if}}
            </:stats>
          </DashboardHero>

          {{#if (eq this.home.audience "new")}}
            {{#if this.home.howItWorks}}
              <Section class="pii" data-test-beta-home-how-panel>
                <:header>{{this.home.howItWorks.title}}</:header>
                <:body>
                  <div class="tio-col gap-4">
                    <ol class="tio-beta-home__grid tio-beta-home__grid--triple">
                      {{#each this.home.howItWorks.steps as |step|}}
                        <li
                          class="tio-col gap-3 tio-card tio-card--radius-card tio-card--padding-sm border tio-border-default"
                          data-test-beta-how-step={{step.id}}
                        >
                          <div class="tio-row-center h-12 w-12 rounded-lg tio-bg-sunken">
                            {{#let step.icon as |Icon|}}
                              <Icon class="tio-icon-xl" />
                            {{/let}}
                          </div>
                          <div class="tio-col-extra-tight">
                            <h3 class="tio-text-base-semibold tio-fg-primary">{{step.title}}</h3>
                            <p class="tio-text-sm-medium tio-fg-secondary">{{step.description}}</p>
                          </div>
                        </li>
                      {{/each}}
                    </ol>
                    <div
                      class="tio-row-between gap-4 tio-card tio-card--radius-card tio-card--padding-sm border tio-border-default"
                      data-test-beta-how-banner
                    >
                      <p class="tio-text-base-medium tio-fg-secondary">
                        {{this.home.howItWorks.banner.prompt}}
                      </p>
                      <Button
                        @route={{this.home.howItWorks.banner.route}}
                        @intent="primary"
                        @size="sm"
                        class="shrink-0"
                        data-test-beta-how-banner-cta
                      >
                        {{this.home.howItWorks.banner.label}}
                      </Button>
                    </div>
                  </div>
                </:body>
              </Section>
            {{/if}}
          {{else}}
            <AttentionPanel
              @title="Needs Your Attention"
              @count={{this.home.attention.rows.length}}
              @emptyTitle={{this.home.attention.emptyTitle}}
              @emptyBody={{this.home.attention.emptyBody}}
              data-test-beta-home-attention-panel
            >
              {{#each this.visibleRows as |row|}}
                <TaskCard
                  @title={{row.title}}
                  @description={{row.description}}
                  @icon={{row.icon}}
                  @urgency={{row.urgency}}
                  @dueLabel={{row.dueLabel}}
                  @actionLabel={{row.cta}}
                  @actionRoute={{this.routeFor row}}
                  @actionModel={{row.model}}
                  class="pii"
                  data-test-beta-alert={{row.id}}
                />
              {{/each}}

              {{#if this.collapsedRowCount}}
                <li class="tio-row-center">
                  <Button
                    @appearance="minimal"
                    @size="sm"
                    @testHook="beta-home__attention-toggle"
                    {{on "click" this.toggleAllRows}}
                    aria-expanded={{if this.isShowingAllRows "true" "false"}}
                    data-test-beta-home-attention-toggle
                  >
                    {{#if this.isShowingAllRows}}
                      Show less
                    {{else}}
                      Show all ({{this.home.attention.rows.length}})
                    {{/if}}
                  </Button>
                </li>
              {{/if}}
            </AttentionPanel>
          {{/if}}

          {{#if this.recommendationItems.length}}
            <Section data-test-beta-home-recommendation-panel>
              <:header>{{t "recommendation_card.heading"}}</:header>
              <:body>
                <RecommendationCard @items={{this.recommendationItems}} />
              </:body>
            </Section>
          {{/if}}

          {{#if this.home.benefitCards.length}}
            <Section data-test-beta-home-active-panel>
              <:header>Active Benefits</:header>
              <:body>
                <div class="tio-beta-home__grid">
                  {{#each this.home.benefitCards as |card|}}
                    <BenefitCard
                      @title={{card.title}}
                      @icon={{card.icon}}
                      @actionLabel={{card.linkLabel}}
                      @actionRoute={{card.route}}
                      class="pii"
                      data-test-beta-benefit={{card.id}}
                    >
                      <div class="tio-col gap-4">
                        <div class="tio-col-extra-tight">
                          <span
                            class="tio-text-2xl-semibold tio-fg-primary"
                            data-test-beta-benefit-value
                          >{{card.headline.value}}</span>
                          <span
                            class="tio-text-sm-medium tio-fg-muted"
                          >{{card.headline.label}}</span>
                        </div>

                        {{#if card.headline.progress}}
                          <ProgressBar
                            @value={{card.headline.progress.value}}
                            @max={{card.headline.progress.max}}
                            @color="primary"
                            @size="sm"
                            @label={{card.title}}
                          />
                        {{/if}}

                        {{#if card.statusLines.length}}
                          <ul
                            class="tio-col-extra-tight tio-text-sm-medium tio-fg-secondary"
                            data-test-beta-benefit-status
                          >
                            {{#each card.statusLines as |line|}}
                              <li>{{line}}</li>
                            {{/each}}
                          </ul>
                        {{/if}}

                        {{#if card.activity.length}}
                          <dl
                            class="tio-col-tight border-t tio-border-default pt-4"
                            data-test-beta-benefit-activity
                          >
                            {{#each card.activity as |row|}}
                              <div class="tio-row-between gap-4">
                                <dt class="tio-text-sm-medium tio-fg-muted">{{row.label}}</dt>
                                <dd class="tio-col-flush text-right">
                                  <span
                                    class="tio-text-sm-semibold tio-fg-primary"
                                  >{{row.value}}</span>
                                  {{#if row.note}}
                                    <span
                                      class="tio-text-xs-normal tio-fg-muted"
                                    >{{row.note}}</span>
                                  {{/if}}
                                </dd>
                              </div>
                            {{/each}}
                          </dl>
                        {{/if}}

                        {{#if card.footerLink}}
                          <LinkTo
                            @route={{card.footerLink.route}}
                            class="tio-row-tight tio-text-sm-semibold app-text-link"
                            data-test-beta-benefit-footer-link
                          >
                            {{card.footerLink.label}}
                            <PhArrowRight class="tio-icon-sm" />
                          </LinkTo>
                        {{/if}}
                      </div>
                    </BenefitCard>
                  {{/each}}
                </div>
              </:body>
            </Section>
          {{/if}}
          {{#if this.home.discover.length}}
            <Section id="beta-home-explore" data-test-beta-home-discover-panel>
              <:header>{{this.discoverHeading}}</:header>
              <:body>
                <div class="tio-beta-home__grid tio-beta-home__grid--quad pii">
                  {{#each this.home.discover as |card|}}
                    <ExploreCard
                      @title={{card.title}}
                      @description={{card.description}}
                      @incentive={{card.incentive}}
                      @icon={{card.icon}}
                      @route={{card.route}}
                      data-test-beta-discover={{card.id}}
                    />
                  {{/each}}
                </div>
              </:body>
            </Section>
          {{/if}}

          <Section data-test-beta-home-resources-panel>
            <:header>Resources</:header>
            <:body>
              <div class="tio-beta-home__grid tio-beta-home__grid--quad pii">
                {{#each this.home.resources as |card|}}
                  <ExploreCard
                    @title={{card.title}}
                    @description={{card.description}}
                    @icon={{card.icon}}
                    @route={{card.route}}
                    data-test-beta-discover={{card.id}}
                  />
                {{/each}}
              </div>
            </:body>
          </Section>
        </VStack>
      </div>
    </div>
  </template>
}
